import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Scissors, Plus, X, Settings, Trash2, RefreshCw, Check, CheckCircle2 } from 'lucide-react';
import { UserProfile, Service } from '../../../types';

interface ServiceManagementProps {
  user: UserProfile;
  onUpdate: (services: any) => void;
  systemServices?: Service[];
}

export const ServiceManagement = ({ user, onUpdate, systemServices = [] }: ServiceManagementProps) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({ name: '', price: 0, duration: 30 });
  const services = user.services || [];

  // Identify services that exist in the system booking catalog but not in this barber's custom services list
  const unsyncedServices = systemServices.filter((sysSvc) => {
    return !services.some(
      (s: any) => s.name.toLowerCase().trim() === sysSvc.name.toLowerCase().trim()
    );
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || formData.price <= 0) return;

    let newServices;
    if (editingId) {
      newServices = services.map((s: any) => s.id === editingId ? { ...formData, id: editingId } : s);
    } else {
      newServices = [...services, { ...formData, id: Date.now().toString() }];
    }

    onUpdate(newServices);
    setIsAdding(false);
    setEditingId(null);
    setFormData({ name: '', price: 0, duration: 30 });
  };

  const handleEdit = (service: any) => {
    setFormData({ name: service.name, price: service.price, duration: service.duration });
    setEditingId(service.id);
    setIsAdding(true);
  };

  const [deletingId, setDeletingId] = useState<string | null>(null);

  const confirmDelete = (id: string) => {
    const newServices = services.filter((s: any) => s.id !== id);
    onUpdate(newServices);
    setDeletingId(null);
  };

  // Merge all missing standard system services into barber custom services
  const handleSyncAll = () => {
    const currentServices = [...services];
    let updated = false;

    systemServices.forEach((sysSvc) => {
      const exists = currentServices.some(
        (s: any) => s.name.toLowerCase().trim() === sysSvc.name.toLowerCase().trim()
      );
      if (!exists) {
        currentServices.push({
          id: sysSvc.id || Date.now().toString() + Math.random().toString(),
          name: sysSvc.name,
          price: sysSvc.price,
          duration: sysSvc.duration || 45
        });
        updated = true;
      }
    });

    if (updated) {
      onUpdate(currentServices);
    }
  };

  // Sync a single standard system service into barber custom services
  const handleSyncIndividual = (sysSvc: Service) => {
    const exists = services.some(
      (s: any) => s.name.toLowerCase().trim() === sysSvc.name.toLowerCase().trim()
    );
    if (!exists) {
      const newServices = [
        ...services,
        {
          id: sysSvc.id || Date.now().toString() + Math.random().toString(),
          name: sysSvc.name,
          price: sysSvc.price,
          duration: sysSvc.duration || 45
        }
      ];
      onUpdate(newServices);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight">Manajemen Layanan</h2>
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Atur jenis gaya & harga yang Anda tawarkan</p>
        </div>
        <button 
          onClick={() => setIsAdding(!isAdding)}
          className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20 active:scale-95 cursor-pointer"
        >
          {isAdding ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {isAdding ? 'Batal' : 'Tambah Baru'}
        </button>
      </div>

      {/* Synchronization Panel */}
      <div className="bg-emerald-50/50 border border-emerald-100 rounded-[2rem] p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-sm">
        <div className="space-y-2 flex-1">
          <div className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-emerald-600 animate-spin-slow" />
            <h3 className="font-bold text-slate-900 text-sm tracking-tight">Sinkronisasi Layanan Booking</h3>
          </div>
          <p className="text-xs text-slate-500 font-semibold leading-relaxed">
            Sinkronkan layanan kustom Anda dengan katalog layanan standar CukurJe agar mempermudah pilihan pelanggan pada menu booking. Semua perubahan harga layanan disini langsung tersinkronkan dengan halaman pemesanan.
          </p>
          
          {/* Individual items pills to easily pick and customize */}
          {unsyncedServices.length > 0 && (
            <div className="pt-2">
              <p className="text-[9px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">Pilih Layanan Standar untuk Dihubungkan:</p>
              <div className="flex flex-wrap gap-2">
                {unsyncedServices.map(sysSvc => (
                  <button
                    key={sysSvc.id || sysSvc.name}
                    onClick={() => handleSyncIndividual(sysSvc)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-emerald-50 border border-slate-200 hover:border-emerald-300 text-[10px] font-black uppercase text-slate-700 rounded-full transition-all shadow-sm active:scale-95 cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5 text-emerald-500" />
                    <span>{sysSvc.name} (Rp {sysSvc.price.toLocaleString()})</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {unsyncedServices.length > 0 ? (
          <button
            onClick={handleSyncAll}
            className="shrink-0 flex items-center gap-2 px-6 py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-extrabold rounded-2xl text-xs uppercase tracking-widest transition-all shadow-xl hover:shadow-emerald-500/20 active:scale-95 cursor-pointer"
          >
            <RefreshCw className="w-4 h-4" />
            Sinkronkan Semua ({unsyncedServices.length})
          </button>
        ) : (
          <div className="shrink-0 flex items-center gap-2 px-5 py-3 bg-emerald-100/40 border border-emerald-200 rounded-2xl text-[10px] font-black text-emerald-800 uppercase tracking-widest shadow-sm">
            <Check className="w-4 h-4 text-emerald-600 stroke-[3px]" />
            Semua Layanan Sinkron
          </div>
        )}
      </div>

      <AnimatePresence>
        {isAdding && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-slate-50 border border-slate-100 rounded-3xl p-6 md:p-8"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-1">
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Nama Layanan</label>
                  <input 
                    type="text"
                    required
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Contoh: Premium Haircut"
                    className="w-full bg-white border border-slate-200 rounded-2xl px-6 py-4 text-sm focus:border-slate-900 outline-none transition-all font-bold"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Harga (Rp)</label>
                  <input 
                    type="number"
                    required
                    value={formData.price}
                    onChange={e => setFormData({ ...formData, price: parseInt(e.target.value) || 0 })}
                    placeholder="50000"
                    className="w-full bg-white border border-slate-200 rounded-2xl px-6 py-4 text-sm focus:border-slate-900 outline-none transition-all font-bold"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Durasi (Menit)</label>
                  <select 
                    value={formData.duration}
                    onChange={e => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                    className="w-full bg-white border border-slate-200 rounded-2xl px-6 py-4 text-sm focus:border-slate-900 outline-none transition-all font-bold appearance-none"
                  >
                    {[15, 30, 45, 60, 90, 120].map(d => (
                      <option key={d} value={d}>{d} Menit</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button 
                  type="button"
                  onClick={() => { setIsAdding(false); setEditingId(null); }}
                  className="px-8 py-3.5 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-900 transition-colors"
                >
                  Batal
                </button>
                <button 
                  type="submit"
                  className="px-8 py-3.5 bg-emerald-500 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-600 transition-all shadow-lg shadow-emerald-500/20 active:scale-95"
                >
                  {editingId ? 'Simpan Perubahan' : 'Tambah Layanan'}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {services.length === 0 ? (
          <div className="md:col-span-2 py-12 text-center bg-white rounded-3xl border border-dashed border-slate-200">
            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Scissors className="w-8 h-8 text-slate-200" />
            </div>
            <p className="text-slate-900 font-bold mb-1">Belum Ada Layanan Kustom</p>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Tambah atau sinkronkan layanan di atas untuk tampil di menu booking.</p>
          </div>
        ) : (
          services.map((s: any) => {
            if (deletingId === s.id) {
              return (
                <div 
                  key={s.id}
                  className="bg-white border-2 border-red-100 rounded-3xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4 transition-all shadow-md"
                >
                  <div className="flex items-center gap-3 text-slate-700 w-full sm:w-auto">
                    <span className="w-10 h-10 bg-red-50 text-red-500 rounded-xl flex items-center justify-center shrink-0">
                      <Trash2 className="w-5 h-5" />
                    </span>
                    <div>
                      <p className="font-bold text-slate-900 text-xs">Hapus "{s.name}"?</p>
                      <p className="text-[10px] font-semibold text-slate-400 mt-0.5">Yakin ingin menghapus layanan ini?</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                    <button 
                      type="button"
                      onClick={() => setDeletingId(null)}
                      className="px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 rounded-xl transition-all cursor-pointer"
                    >
                      Batal
                    </button>
                    <button 
                      type="button"
                      onClick={() => confirmDelete(s.id)}
                      className="px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-white bg-red-500 hover:bg-red-600 rounded-xl transition-all shadow-md shadow-red-500/10 cursor-pointer"
                    >
                      Hapus
                    </button>
                  </div>
                </div>
              );
            }

            return (
              <div 
                key={s.id}
                className="bg-white border border-slate-100 rounded-3xl p-6 flex items-center justify-between group hover:border-slate-300 transition-all shadow-sm hover:shadow-md"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-400 group-hover:bg-slate-900 group-hover:text-white transition-all">
                    <Scissors className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">{s.name}</h3>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-[10px] font-black text-emerald-500 uppercase tracking-widest">Rp {s.price.toLocaleString()}</span>
                      <span className="w-1 h-1 bg-slate-200 rounded-full" />
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{s.duration}m</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 transition-all">
                  <button 
                    onClick={() => handleEdit(s)}
                    className="w-10 h-10 bg-slate-50 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-xl flex items-center justify-center transition-all cursor-pointer"
                  >
                    <Settings className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => setDeletingId(s.id)}
                    className="w-10 h-10 bg-slate-50 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl flex items-center justify-center transition-all cursor-pointer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
